import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { PaletteColorKey } from "../../styles/theme";

type StepStatus = "completed" | "active" | "upcoming";

export interface ProgressStep {
  /** optional icon override; falls back to the status default */
  icon?: React.ReactNode;
  label: string;
}

export interface ProgressIndicatorProps {
  steps: ProgressStep[];
  /** 0-based index of the current step */
  activeStep: number;
  iconSize?: number; // px diameter of each node icon
  lineThickness?: number; // px thickness of the connector
  colorOverride?: PaletteColorKey;
  statusOverride?: "hold" | "void";
}

function getStatus(index: number, activeStep: number): StepStatus {
  if (index < activeStep) return "completed";
  if (index === activeStep) return "active";
  return "upcoming";
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  activeStep,
  iconSize = 28,
  lineThickness = 3,
  colorOverride,
  statusOverride,
}) => {
  const theme = useTheme();

  const labelRefs = React.useRef<(HTMLElement | null)[]>([]);

  const [connectorWidth, setConnectorWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const measure = () => {
      const widest = labelRefs.current.reduce((max, el) => {
        const w = el ? el.getBoundingClientRect().width : 0;
        return w > max ? w : max;
      }, 0);
      const next = Math.ceil(widest / 2); // round up so sub-pixel widths can't cause a 1px overlap
      setConnectorWidth((prev) => (prev === next ? prev : next)); // skip no-op updates
    };

    measure();

    // Web fonts can change text width after first paint — re-measure once they're ready
    let active = true;
    if (document.fonts?.status !== "loaded") {
      document.fonts?.ready.then(() => active && measure());
    }

    // Re-measure if a label's size changes (responsive font, locale change, etc.)
    const ro = new ResizeObserver(measure);
    labelRefs.current.forEach((el) => el && ro.observe(el));

    return () => {
      active = false;
      ro.disconnect();
    };
  }, [steps]); // re-run when the labels change

  const iconColor = (status: StepStatus): string => {
    if (colorOverride) return theme.palette[colorOverride].main;
    if (statusOverride === "hold") return theme.palette.holding.main;
    if (statusOverride === "void") return theme.palette.void.main;
    return status === "upcoming"
      ? theme.palette.grey[400]
      : theme.palette.primary.main;
  };

  const connectorColor = (filled: boolean): string => {
    if (colorOverride) return theme.palette[colorOverride].main;
    if (statusOverride === "hold") return theme.palette.holding.light;
    if (statusOverride === "void") return theme.palette.void.light;
    return filled ? theme.palette.primary.light : theme.palette.grey[300];
  };

  const renderIcon = (status: StepStatus, step: ProgressStep) => {
    if (step.icon) return step.icon;
    if (status === "completed") return <CheckCircleIcon />;
    if (status === "active") return <RadioButtonCheckedIcon />;
    return <RadioButtonUncheckedIcon />;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "fit-content",
        px: connectorWidth / 2,
      }}
    >
      {steps.map((step, index) => {
        const status = getStatus(index, activeStep);
        const isLast = index === steps.length - 1;
        // the connector AFTER this node is part of the fill once the node is done
        const connectorFilled = index < activeStep;

        return (
          <React.Fragment key={index}>
            {/* Single Stack; fixed width = iconSize so labels never change spacing */}
            <Stack
              alignItems="center"
              sx={{
                flex: "0 0 auto",
                width: iconSize,
                height: iconSize,
                color: iconColor(status),
                "& svg": { fontSize: iconSize, display: "block" },
              }}
            >
              {renderIcon(status, step)}
              <Typography
                ref={(el) => {
                  // ELEMENT REF ARRAY
                  labelRefs.current[index] = el;
                }}
                variant="body2"
                sx={{
                  mt: 0.5,
                  width: "max-content", // sizes to text, overflows the node
                  whiteSpace: "nowrap", // ...without wrapping or widening it
                  color: "text.primary", // don't tint the label with the node color
                }}
              >
                {step.label}
              </Typography>
            </Stack>

            {!isLast && (
              <Box
                sx={{
                  flex: "0 0 auto",
                  mt: `${(iconSize - lineThickness) / 2}px`, // centers line on icon
                  height: lineThickness,
                  width: connectorWidth,
                  borderRadius: lineThickness,
                  backgroundColor: connectorColor(connectorFilled),
                  transition: "background-color 300ms ease",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default ProgressIndicator;
